import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { Chart, ArcElement, Tooltip, Legend, Title } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Doughnut } from 'react-chartjs-2'
import CountUp from 'react-countup'

import { getResourceChart, getPieChartOptions, getUserCountryChart, getUserInstitutionChart, getTagCloud } from './chartConfigs'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'

import Loading from '../loading/Loading'

import { TagCloud } from 'react-tagcloud'

import { PAGES } from '../navigation/routes'

import { setErrorNotification } from '../../reducers/notificationReducer'
import adminService from '../../services/admin'
import { AdminStyle } from '../styles'

Chart.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels)

const EMPTY_SUMMARY = {
    users_count: 0,
    tags_count: 0,
    approved_resources_count: 0,
    pending_resources_count: 0,
    user_country_chart: [],
    user_institution_chart: [],
    approved_resource_chart: [],
    pending_resource_chart: [],
    top_rated_resources: [],
    bottom_rated_resources: [],
    tag_cloud: [],
}

const Admin = () => {
    const user = useSelector(state => state.user)
    const [summary, setSummary] = useState(null)
    const dispatch = useDispatch()

    useEffect(() => {
        let isMounted = true

        const loadSummary = async () => {
            try {
                const data = await adminService.getSummary()
                if (isMounted) {
                    setSummary(data)
                }
            } catch {
                dispatch(setErrorNotification('Error retrieving admin analytics'))
                if (isMounted) {
                    setSummary(EMPTY_SUMMARY)
                }
            }
        }

        // RequireStaff (App.js) guarantees this component only mounts for a
        // loaded, staff user — this check just guards against `user` being
        // momentarily stale on the very first effect run.
        if (user?.staff) {
            loadSummary()
        }

        return () => {
            isMounted = false
        }
    }, [user, dispatch])

    const customRenderer = (tag, size, color) => (
        <span key={tag.value}>
            <MUITooltip title={`Appearance: ${tag.count}`}>
                <Box
                    sx={{
                        fontSize: size,
                        display: 'inline-block',
                        color: color,
                        transition: 'ease 500ms',
                        '&:hover': {
                            textShadow: '1px 1px 2px #999999'
                        }
                    }}
                >
                    {tag.value}
                </Box>
            </MUITooltip>
        </span>
    )

    if (summary === null) {
        return (
            <Container {...AdminStyle.loading.props} sx={AdminStyle.loading.style}>
                <Loading />
            </Container>
        )
    }

    return (
        <>
            <Container {...AdminStyle.container.props} sx={AdminStyle.container.style}>
                <Typography {...AdminStyle.title}  gutterBottom>
                    Administration
                </Typography>
                <Box sx={AdminStyle.secondary}>
                    <Typography {...AdminStyle.secondaryText} gutterBottom>
                        Welcome to the Admin Portal
                    </Typography>
                </Box>
                <Grid container spacing={2} alignItems={'center'} justifyContent={'center'}>
                    <Grid item xs={12} sm={6} md={2}>
                        <Link to={PAGES.create_resource.path} style={AdminStyle.navLink}>
                            <Box sx={AdminStyle.navContainer}>
                                <Typography variant='p'>
                                    Create Resource
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Link to={PAGES.manage_resources.path} style={AdminStyle.navLink}>
                            <Box sx={AdminStyle.navContainer}>
                                <Typography variant='p'>
                                    {PAGES.manage_resources.title}
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Link to={PAGES.manage_tags.path} style={AdminStyle.navLink}>
                            <Box sx={AdminStyle.navContainer}>
                                <Typography variant='p'>
                                    {PAGES.manage_tags.title}
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Link to={PAGES.manage_admin.path} style={AdminStyle.navLink}>
                            <Box sx={AdminStyle.navContainer}>
                                <Typography variant='p'>
                                    {PAGES.manage_admin.title}
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Link to={PAGES.manage_automation.path} style={AdminStyle.navLink}>
                            <Box sx={AdminStyle.navContainer}>
                                <Typography variant='p'>
                                    {PAGES.manage_automation.title}
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                </Grid>
            </Container>
            <Container {...AdminStyle.analytics.props} sx={AdminStyle.analytics.style}>
                <Typography sx={AdminStyle.analyticsTitle.style} {...AdminStyle.analyticsTitle.props} gutterBottom>
                    Site Analytics
                </Typography>
                <Grid container spacing={4} sx={AdminStyle.analyticsGrid}>
                    <Grid item xs={12} md={6} sx={AdminStyle.analyticsNumContainer}>
                        <Box sx={AdminStyle.analyticsNum}>
                            <Typography {...AdminStyle.analyticsNumText} gutterBottom>
                                <CountUp end={summary.users_count} /> Users
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={AdminStyle.analyticsNumContainer}>
                        <Box sx={AdminStyle.analyticsNum}>
                            <Typography {...AdminStyle.analyticsNumText} gutterBottom>
                                <CountUp end={summary.tags_count} /> Tags
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={AdminStyle.analyticsNumContainer}>
                        <Box sx={AdminStyle.analyticsNum}>
                            <Typography {...AdminStyle.analyticsNumText} gutterBottom>
                                <CountUp end={summary.approved_resources_count} /> Approved Resources
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={AdminStyle.analyticsNumContainer}>
                        <Box sx={AdminStyle.analyticsNum}>
                            <Typography {...AdminStyle.analyticsNumText} gutterBottom>
                                <CountUp end={summary.pending_resources_count} /> Pending Resources
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={AdminStyle.graphContainer}>
                            <Doughnut data={getUserCountryChart(summary.user_country_chart || [])} options={getPieChartOptions(`Users' Country Breakdown`)} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={AdminStyle.graphContainer}>
                            <Doughnut data={getUserInstitutionChart(summary.user_institution_chart || [])} options={getPieChartOptions(`Users' Institution Breakdown`)} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={AdminStyle.graphContainer}>
                            {summary.approved_resource_chart.length > 0
                                ? <Doughnut data={getResourceChart(summary.approved_resource_chart)} options={getPieChartOptions(`Approved Resources (Total: ${summary.approved_resources_count})`)} />
                                : <Typography variant='h4' gutterBottom>There are no resources currently!</Typography>}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={AdminStyle.graphContainer}>
                            {summary.pending_resource_chart.length > 0
                                ? <Doughnut data={getResourceChart(summary.pending_resource_chart)} options={getPieChartOptions(`Pending Resources (Total: ${summary.pending_resources_count})`)} />
                                : <Typography variant='h4' gutterBottom>There are no pending resources!</Typography>}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={AdminStyle.rating}>
                            <Typography {...AdminStyle.ratingTitle} gutterBottom>Tag Presence Across Hub</Typography>
                            <Box>
                                <TagCloud
                                    minSize={15}
                                    maxSize={100}
                                    tags={getTagCloud(summary.tag_cloud || [])}
                                    colorOptions={{
                                        luminosity: 'dark',
                                        format: 'rgba',
                                        alpha: 0.5
                                    }}
                                    renderer={customRenderer}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </>
    )
}

export default Admin
